import {
  Ticket,
  SaleTicket,
  Spot,
  PageInfo,
  SystemMessage,
  Meta,
  Topic,
  Tag,
  TagCategory,
  Special,
  BannerAd,
} from "../global-commons/type";
import { Languages, DEFAULT_LANGUAGE } from "./i18n/base";
import { InternalServerError } from "../global-commons/errors";

/** parameters of listTickets API
 */
export type ListTicketParams = {
  tagCategoryIds?: number[];
  tagIds?: number[];
  cnt?: number; // number of tickets per page
  page?: number; // page number of search result, 1-indexed
};

export async function listTickets(
  params: ListTicketParams,
  apiUrl: string,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<{ tickets: Ticket[]; pageInfo: PageInfo }> {
  // ordered by created date in descending order
  const searchParams = new URLSearchParams({
    cnt: (params.cnt ?? "12").toString(),
    pageID: params.page ? params.page.toString() : "1",
    _lang: lang,
  });

  if (params.tagCategoryIds) {
    for (const categoryId of params.tagCategoryIds) {
      searchParams.append("tag_category_id[]", categoryId.toString());
    }
  }

  if (params.tagIds) {
    for (const tagId of params.tagIds) {
      searchParams.append("tag_id[]", tagId.toString());
    }
  }

  // tag / tag_category の検索条件をANDに設定
  searchParams.append("tag_cond", "AND");

  console.log("searchParams", searchParams.toString());

  const res = await fetch(apiUrl + "/tickets?" + searchParams.toString());
  const ticketsRaw = await res.json();
  if (ticketsRaw.errors.length > 0) {
    throw new InternalServerError(
      ticketsRaw.errors[0].message,
      "GET /tickets?" + searchParams.toString()
    );
  }
  return { tickets: ticketsRaw.list, pageInfo: ticketsRaw.pageInfo };
}

export type ListAllTicketParams = Omit<ListTicketParams, "page" | "cnt">;

/** 全ページのチケットを一括取得する。 */
export async function listAllTickets(
  params: ListAllTicketParams,
  apiUrl: string,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<Ticket[]> {
  let tickets: Ticket[] = [];
  let page = 1;
  const cnt = 100; // 1ページあたりの取得件数
  let pageInfo: PageInfo;
  do {
    const res = await listTickets({ ...params, page, cnt }, apiUrl, lang);
    tickets = tickets.concat(res.tickets);
    pageInfo = res.pageInfo;
    page++;
  } while (page <= pageInfo.totalPageCnt);
  return tickets;
}

export async function getTicket(
  id: string,
  apiUrl: string,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<Ticket | null> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(
    apiUrl + "/ticket/" + id + "?" + searchParams.toString()
  );
  const ticketData = await res.json();

  // error handling
  if (ticketData.errors.length > 0) {
    if (ticketData.errors[0].code === "not_found") {
      return null;
    }
    throw new Error(ticketData.errors[0].message);
  }

  return ticketData.details as Ticket;
}

export type ListSaleTicketParams = {
  cnt?: number; // number of tickets per page
  page?: number; // page number of search result, 1-indexed
  tagIds?: number[];
  tagCategoryIds?: number[];
  keywords?: string[]; // search keywords
  showOutdated?: "all" | "on-sale" | "not-sale"; // all: 全てのチケットを表示する
  // on-sale: 現在販売中のチケットのみを表示する
  // not-sale: 現在販売中ではないチケットのみを表示する,デフォルトはon-sale
};
export async function listSaleTickets(
  params: ListSaleTicketParams,
  apiUrl: string,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<{ saleTickets: SaleTicket[]; pageInfo: PageInfo }> {
  const searchParams = new URLSearchParams({
    cnt: (params.cnt ?? "12").toString(),
    pageID: params.page ? params.page.toString() : "1",
    _lang: lang,
  });

  if (params.tagCategoryIds) {
    for (const categoryId of params.tagCategoryIds) {
      searchParams.append("tag_category_id[]", categoryId.toString());
    }
  }

  if (params.tagIds) {
    for (const tagId of params.tagIds) {
      searchParams.append("tag_id[]", tagId.toString());
    }
  }

  /**  フィルター条件 */
  const filterElements: string[] = [];

  if (params.keywords) {
    for (const keyword of params.keywords) {
      filterElements.push(`subject contains "${keyword}"`);
    }
  }

  if (params.showOutdated === "on-sale" || params.showOutdated === undefined) {
    filterElements.push('saleStartAt <:relatively "now"');
    filterElements.push('saleEndAt >:relatively "now"');
  } else if (params.showOutdated === "not-sale") {
    filterElements.push(
      'saleStartAt >:relatively "now" OR saleEndAt <:relatively "now"'
    );
  }
  // 全てのチケットを表示する場合(`all`)はフィルターを追加しない

  if (filterElements.length > 0) {
    searchParams.append("filter", "(" + filterElements.join(" AND ") + ")");
  }

  const res = await fetch(apiUrl + "/sale-tickets?" + searchParams.toString());
  const saleTicketsRaw = await res.json();
  if (saleTicketsRaw.errors.length > 0) {
    throw new InternalServerError(
      saleTicketsRaw.errors[0].message,
      "GET /sale-tickets?" + searchParams.toString()
    );
  }
  return {
    saleTickets: saleTicketsRaw.list,
    pageInfo: saleTicketsRaw.pageInfo,
  };
}

export type ListAllSaleTicketParams = Omit<
  ListSaleTicketParams,
  "page" | "cnt"
>;

export async function listAllSaleTickets(
  apiUrl: string,
  params: ListAllSaleTicketParams,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<SaleTicket[]> {
  let saleTickets: SaleTicket[] = [];
  let page = 1;
  const cnt = 100; // 1ページあたりの取得件数
  let pageInfo: PageInfo;
  do {
    const res = await listSaleTickets({ ...params, page, cnt }, apiUrl, lang);
    saleTickets = saleTickets.concat(res.saleTickets);
    pageInfo = res.pageInfo;
    page++;
  } while (page <= pageInfo.totalPageCnt);
  return saleTickets;
}

/** 販売チケットを取得する
 * @param id 販売チケットのID
 * @param apiUrl APIのURL
 * @param lang 言語コード
 * @returns 販売チケットの情報。
 */
export async function getSaleTicket(
  id: string,
  apiUrl: string,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<SaleTicket | null> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(
    apiUrl + "/sale-ticket/" + id + "?" + searchParams.toString()
  );
  const saleTicketData = await res.json();

  // error handling
  if (saleTicketData.errors.length > 0) {
    if (saleTicketData.errors[0].code === "not_found") {
      return null;
    }
    throw new Error(saleTicketData.errors[0].message);
  }

  return saleTicketData.details as SaleTicket;
}

export type ListSpotParams = {
  cnt?: number; // number of tickets per page
  page?: number; // page number of search result, 1-indexed
  tagCategoryIds?: number[];
  tagIds?: number[];
  keyword?: string[]; // search keyword
};

export async function listSpots(
  params: ListSpotParams,
  apiUrl: string,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<{ spots: Spot[]; pageInfo: PageInfo }> {
  const searchParams = new URLSearchParams({
    cnt: (params.cnt ?? "12").toString(),
    pageID: params.page ? params.page.toString() : "1",
    _lang: lang,
  });

  if (params.tagCategoryIds) {
    for (const categoryId of params.tagCategoryIds) {
      searchParams.append("tag_category_id[]", categoryId.toString());
    }
  }

  if (params.tagIds) {
    for (const tagId of params.tagIds) {
      searchParams.append("tag_id[]", tagId.toString());
    }
  }

  if (params.keyword) {
    const filterElements: string[] = [];
    for (const keyword of params.keyword) {
      filterElements.push(`subject contains "${keyword}"`);
    }
    searchParams.append("filter", "(" + filterElements.join(" AND ") + ")");
  }

  const res = await fetch(apiUrl + "/spots?" + searchParams.toString());
  const saleTicketsRaw = await res.json();
  if (saleTicketsRaw.errors.length > 0) {
    throw new InternalServerError(
      saleTicketsRaw.errors[0].message,
      "GET /spots?" + searchParams.toString()
    );
  }
  return { spots: saleTicketsRaw.list, pageInfo: saleTicketsRaw.pageInfo };
}

export type ListAllSpotParams = Omit<ListSpotParams, "page" | "cnt">;

export async function listAllSpots(
  apiUrl: string,
  params: ListAllSpotParams,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<Spot[]> {
  let saleTickets: Spot[] = [];
  let page = 1;
  const cnt = 100; // 1ページあたりの取得件数
  let pageInfo: PageInfo;
  do {
    const res = await listSpots({ ...params, page, cnt }, apiUrl, lang);
    saleTickets = saleTickets.concat(res.spots);
    pageInfo = res.pageInfo;
    page++;
  } while (page <= pageInfo.totalPageCnt);
  return saleTickets;
}

/** スポットを取得する
 * @param id スポットのID
 * @param apiUrl APIのURL
 * @param lang 言語コード
 * @returns スポットの情報。
 */
export async function getSpot(
  id: string,
  apiUrl: string,
  lang: Languages = DEFAULT_LANGUAGE
): Promise<Spot | null> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(
    apiUrl + "/spot/" + id + "?" + searchParams.toString()
  );
  const spotData = await res.json();

  // error handling
  if (spotData.errors.length > 0) {
    if (spotData.errors[0].code === "not_found") {
      return null;
    }
    throw new Error(spotData.errors[0].message);
  }
  return spotData.details as Spot;
}

export async function listSystemMessages(
  cnt: number,
  apiUrl: string,
  lang: Languages
): Promise<SystemMessage[]> {
  const searchParams = new URLSearchParams({
    _lang: lang,
    cnt: cnt.toString(),
    // サーバ側で一次ソート (複数キー指定可能な場合)
    order_query: "regular_flg=DESC,ymd=DESC,update_ymdhi=DESC",
  });

  const res = await fetch(
    apiUrl + "/system-messages?" + searchParams.toString()
  );
  const data = await res.json();
  const list = data.list as SystemMessage[];

  // 念のためクライアント側でも最終ソート規則を適用
  list.sort((a, b) => {
    const ra = (a as SystemMessage).regular_flg ?? 0;
    const rb = (b as SystemMessage).regular_flg ?? 0;
    if (rb !== ra) return rb - ra; // regular_flg=1 を先頭

    const yA = (a as SystemMessage).ymd ?? "";
    const yB = (b as SystemMessage).ymd ?? "";
    if (yB !== yA) return yB.localeCompare(yA); // ymd 降順

    const uA = (a as SystemMessage).update_ymdhi ?? "";
    const uB = (b as SystemMessage).update_ymdhi ?? "";
    return uB.localeCompare(uA); // update_ymdhi 降順
  });

  return list;
}

export async function getSystemMessage(
  id: string,
  apiUrl: string,
  lang: Languages
): Promise<SystemMessage | null> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(
    apiUrl + "/system-message/" + id + "?" + searchParams.toString()
  );
  const SystemMessageData = await res.json();

  // error handling
  if (SystemMessageData.errors.length > 0) {
    if (SystemMessageData.errors[0].code === "not_found") {
      return null;
    }
    throw new Error(SystemMessageData.errors[0].message);
  }

  return SystemMessageData.details as SystemMessage;
}

export async function listMeta(apiUrl: string, lang: Languages): Promise<Meta> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(apiUrl + "/meta?" + searchParams.toString());
  const data = await res.json();
  return data.list[0] as Meta;
}

export async function listAllTopics(
  apiUrl: string,
  lang: Languages
): Promise<{ topics: Topic[]; pageInfo: PageInfo }> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(apiUrl + "/topics?" + searchParams.toString());
  const data = await res.json();
  return { topics: data.list, pageInfo: data.pageInfo };
}

export async function getSpecial(
  id: string,
  apiUrl: string,
  lang: Languages
): Promise<Special | null> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(
    apiUrl + "/special/" + id + "?" + searchParams.toString()
  );
  const data = await res.json();
  return data.details as Special;
}

export async function listAllSpecials(
  apiUrl: string,
  lang: Languages
): Promise<Special[]> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(apiUrl + "/specials?" + searchParams.toString());
  const data = await res.json();
  return data.list as Special[];
}

export async function listAllBannerAds(
  apiUrl: string,
  lang: Languages
): Promise<BannerAd[]> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });
  const res = await fetch(apiUrl + "/banner-ads?" + searchParams.toString());
  const data = await res.json();
  return data.list as BannerAd[];
}

/** filter parameters of listTickets API
 * In website, user filters tickets by category and area.
 * In logic, area are decomposed into prefectures for search purpose.
 */
export type ListTagsParams = {
  categoryIds?: number[];
};

/** 全てのタグを返す */
export async function listAllTags(
  apiUrl: string,
  params: ListTagsParams,
  lang: Languages
): Promise<Tag[]> {
  const searchParams = new URLSearchParams({
    _lang: lang,
  });

  if (params.categoryIds) {
    for (const categoryId of params.categoryIds) {
      searchParams.append("category_id[]", categoryId.toString());
    }
  }

  const res = await fetch(apiUrl + "/tags?" + searchParams.toString());
  const tagsRaw = await res.json();
  if (tagsRaw.errors.length > 0) {
    throw new InternalServerError(
      tagsRaw.errors[0].message,
      "GET /tags?" + searchParams.toString()
    );
  }

  const tags = tagsRaw.list;
  return tags;
}

/** 全てのタグカテゴリを返す */
export async function listAllTagCategories(
  apiUrl: string,
  lang: Languages
): Promise<TagCategory[]> {
  const searchParams = new URLSearchParams({
    _lang: lang,
    _doc_lang: lang, // 追加: ドキュメント言語指定
  });

  const res = await fetch(
    apiUrl + "/tag-categories?" + searchParams.toString()
  );
  const tagCategoryRaw = await res.json();
  if (tagCategoryRaw.errors.length > 0) {
    throw new InternalServerError(
      tagCategoryRaw.errors[0].message,
      "GET /tag-categories?" + searchParams.toString()
    );
  }

  const tagCategories = tagCategoryRaw.list;
  return tagCategories;
}
